import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { isNil } from 'ramda'

import { FIRST_25_VARIABLES } from 'constants/pagination'
import { PROVIDER, FACILITY } from 'constants/constants'
import useScroll from 'utils/hooks/useScroll'
import { getNodes, getPageInfo } from 'utils/paginationHelper'
import { verifiedValues } from 'components/VerifiedDropdown/helpers'
import { convertVerifiedAfterToSeconds } from 'utils/helpers/filtersHelper'
import { NODES_PATH } from 'constants/dataPaths'
import { parseNonZeroJson } from 'utils/strings'

import PROVIDERS_PAGE from 'apollo/queries/PROVIDERS_PAGE'
import FACILITIES_PAGE from 'apollo/queries/FACILITIES_PAGE'

const useEntitiesTable = (entityType, filterValues, employee_id) => {
  const isProviderSearch = entityType === PROVIDER
  const isFacilitySearch = entityType === FACILITY

  let query = PROVIDERS_PAGE
  let dataPath = ['']
  let params = {}

  const {
    address,
    min_rating,
    name,
    location_types,
    is_verified,
    verified_after,
    distance,
    npi,
    specialties_uuids,
    gender,
    language,
    plan_id,
    min_confidence,
    insurance_plan_tiers
  } = filterValues

  const verifiedAfter = useMemo(() => {
    const date = verifiedValues.find(
      item => item.value === verified_after
    )?.subtract
    return convertVerifiedAfterToSeconds(date)
  }, [verified_after])

  const minConfidence = isNil(min_confidence)
    ? null
    : parseInt(min_confidence, 10)

  if (isProviderSearch) {
    const hasPlanId = !!plan_id
    const hasTiers = !!insurance_plan_tiers && !!insurance_plan_tiers?.length

    const distanceNum = distance ? parseInt(distance, 10) : undefined

    dataPath = ['ribbon_health_doctors_without_defaults_v2']
    params = {
      member_has_plan: hasPlanId,
      ...(hasPlanId && { member_plan_id: plan_id }),
      employee_id: !!employee_id ? employee_id : undefined,
      filters: {
        min_location_confidence: minConfidence,
        ...(npi && { npi: [npi] }),
        ...(name && { name }),
        is_verified,
        verified_after: verifiedAfter,
        specialties_uuids: parseNonZeroJson(specialties_uuids),
        distance: distanceNum,
        gender,
        language,
        address_in_one_line: address,
        min_rating: min_rating ? +min_rating : undefined,
        ...(hasPlanId &&
          hasTiers && {
            insurance_plan_tiers: {
              kinds: JSON.parse(insurance_plan_tiers),
              plan_id
            }
          })
      }
    }
  }

  if (isFacilitySearch) {
    query = FACILITIES_PAGE
    dataPath = ['facilities_v2']
    params = {
      member_has_plan: false,
      employee_id: !!employee_id ? employee_id : undefined,
      filters: {
        address_in_one_line: address,
        confirmed: is_verified,
        confirmed_after: verifiedAfter,
        distance: distance && +distance,
        name,
        min_confidence: minConfidence,
        ...(location_types && { location_types: JSON.parse(location_types) })
      }
    }
  }

  const variables = {
    ...FIRST_25_VARIABLES,
    ...params
  }

  const { data, loading, fetchMore } = useQuery(query, {
    variables,
    skip: !entityType
  })

  const pageInfo = getPageInfo(dataPath)(data)

  const { scrollLoading } = useScroll({
    dataPath,
    dataType: NODES_PATH,
    fetchMore,
    pageInfo,
    isTable: true
  })

  const entities = getNodes(dataPath)(data)

  return {
    data: {
      loading,
      entities,
      scrollLoading
    }
  }
}

export default useEntitiesTable
